import type { FieldErrors } from '@src/modules/kernel/FieldErrors.ts';
import type { EntregaRemisionFormState } from './EntregaRemisionFormState.ts';

export const ENTREGA_REMISION_MAX_SOLICITADAS = 2000;
export const ENTREGA_REMISION_VEHICULO_PLATE_PATTERN = /^[A-Z]{3}\d{3}$/;

/** Colombian plate while typing: LLLNNN, uppercase, max 6 chars. */
export function normalizeEntregaRemisionVehiculoInput(value: string): string {
  const upper = value.toUpperCase();
  let letters = '';
  let digits = '';
  for (const ch of upper) {
    if (letters.length < 3) {
      if (/[A-Z]/.test(ch)) letters += ch;
    } else if (digits.length < 3) {
      if (/\d/.test(ch)) digits += ch;
    }
  }
  return letters + digits;
}

export function clampEntregaRemisionSolicitadas(value: number): number {
  const n = Math.trunc(Number(value) || 0);
  return Math.min(ENTREGA_REMISION_MAX_SOLICITADAS, Math.max(0, n));
}

export type EntregaRemisionValidationOpts = {
  existingFirmaRecibeMediaId?: string | null;
  existingFirmaEntregaMediaId?: string | null;
  participanteCount?: number;
  requiresParticipantes?: boolean;
};

export function validateEntregaRemisionFormErrors(
  state: EntregaRemisionFormState,
  opts?: EntregaRemisionValidationOpts,
): FieldErrors {
  const errors: FieldErrors = {};
  if (!state.nombreDespacha.trim()) errors['nombre-despacha'] = 'Indique quién despacha.';
  const vehiculo = state.vehiculo.trim();
  if (!vehiculo) errors['vehiculo'] = 'Indique el vehículo que entrega.';
  else if (!ENTREGA_REMISION_VEHICULO_PLATE_PATTERN.test(vehiculo)) {
    errors['vehiculo'] = 'Placa inválida. Use 3 letras y 3 números (ej. ABC123).';
  }
  if (!state.fechaHora.trim()) errors['fecha-hora'] = 'Indique fecha y hora de entrega.';
  if (state.cantidadSolicitadas < 0) errors['cantidad-solicitadas'] = 'Cantidad solicitada inválida.';
  else if (state.cantidadSolicitadas > ENTREGA_REMISION_MAX_SOLICITADAS) {
    errors['cantidad-solicitadas'] = 'La cantidad solicitada no puede superar 2.000.';
  }
  if (state.cantidadEntregadas < 0) errors['cantidad-entregadas'] = 'Cantidad entregada inválida.';
  if (opts?.participanteCount !== undefined) {
    const requiresParticipantes = opts.requiresParticipantes !== false;
    if (requiresParticipantes) {
      if (opts.participanteCount < 1) {
        errors['cantidad-entregadas'] = 'Agregue participantes antes de registrar la remisión.';
      } else if (state.cantidadEntregadas !== opts.participanteCount) {
        errors['cantidad-entregadas'] = `Debe coincidir con el número de participantes (${opts.participanteCount}).`;
      }
    } else if (opts.participanteCount > 0 && state.cantidadEntregadas !== opts.participanteCount) {
      errors['cantidad-entregadas'] = `Debe coincidir con el número de participantes (${opts.participanteCount}).`;
    }
  }
  if (state.cantidadBolsasSolicitadas < 0) errors['bolsas-solicitadas'] = 'Cantidad de bolsas solicitada inválida.';
  else if (state.cantidadBolsasSolicitadas > ENTREGA_REMISION_MAX_SOLICITADAS) {
    errors['bolsas-solicitadas'] = 'La cantidad de bolsas solicitada no puede superar 2.000.';
  }
  if (state.cantidadBolsasEntregadas < 0) errors['bolsas-entregadas'] = 'Cantidad de bolsas entregada inválida.';
  else if (state.cantidadBolsasEntregadas > ENTREGA_REMISION_MAX_SOLICITADAS) {
    errors['bolsas-entregadas'] = 'La cantidad de bolsas entregada no puede superar 2.000.';
  }
  if (!state.nombreRecibe.trim()) errors['nombre-recibe'] = 'Indique quién recibe.';
  if (!state.cedulaRecibe.trim()) errors['cedula-recibe'] = 'Indique la cédula de quien recibe.';
  if (!state.nombreEntrega.trim()) errors['nombre-entrega'] = 'Indique quién entrega.';
  if (!state.cedulaEntrega.trim()) errors['cedula-entrega'] = 'Indique la cédula de quien entrega.';
  if (!state.firmaRecibeBlob && !opts?.existingFirmaRecibeMediaId?.trim()) {
    errors['firma-recibe'] = 'Capture la firma de quien recibe.';
  }
  if (!state.firmaEntregaBlob && !opts?.existingFirmaEntregaMediaId?.trim()) {
    errors['firma-entrega'] = 'Capture la firma de quien entrega.';
  }
  return errors;
}
